import { AUDIO_GAIN, HUE_BASE, HUE_SWING, STATE_PRESETS, TIMING } from "./orb.constants.js";
import type { OrbAudioFrame, OrbMotion, OrbState } from "./orb.types.js";

const ease = (dt: number, tau: number): number => 1 - Math.exp(-dt / tau);

/**
 * Eases the shader parameters toward the current state's targets, and turns the raw audio level
 * into a smooth "intensity". Lives outside React: it is stepped once per animation frame.
 */
export class OrbMotionController {
  readonly motion: OrbMotion = { ...STATE_PRESETS.idle, audioIntensity: 0, hue: 0, bandLow: 0, bandMid: 0, bandHigh: 0 };
  private clock = 0;
  private huePhase = 0;
  private previous: OrbState = "idle";

  update(dt: number, state: OrbState, audio: OrbAudioFrame): OrbMotion {
    this.clock += dt;
    const m = this.motion;

    // Audio: fast attack, slow release, so a pause in speech lets the orb settle gradually.
    const a = Math.max(0, Math.min(1, audio.level));
    m.audioIntensity += (a - m.audioIntensity) * ease(dt, a > m.audioIntensity ? TIMING.audioAttack : TIMING.audioRelease);

    // Each part of the voice lights its own colour; they follow the voice quickly and let go slowly.
    const band = (cur: number, target: number) => cur + (Math.min(1, target) - cur) * ease(dt, target > cur ? 0.08 : 0.4);
    m.bandLow = band(m.bandLow, audio.low);
    m.bandMid = band(m.bandMid, audio.mid);
    m.bandHigh = band(m.bandHigh, audio.high);

    const base = STATE_PRESETS[state];
    const speaking = state === "speaking";
    const g = speaking ? m.audioIntensity : 0;

    // Leaving speech takes longer than entering a state, so it never looks like a cut.
    const tau = this.previous === "speaking" && !speaking ? TIMING.paramEaseLeavingSpeech : TIMING.paramEase;
    const k = ease(dt, tau);

    // Slow breathing at rest, a quick pulse while listening.
    const breathe = state === "idle" ? 0.012 * Math.sin(this.clock * 0.9) : state === "listening" ? 0.016 * Math.sin(this.clock * 3.2) : 0;

    m.scale += (base.scale + breathe + AUDIO_GAIN.scale * g - m.scale) * k;
    m.turbulence += (base.turbulence + AUDIO_GAIN.turbulence * g - m.turbulence) * k;
    m.flowSpeed += (base.flowSpeed + AUDIO_GAIN.flowSpeed * g - m.flowSpeed) * k;
    m.glowStrength += (base.glowStrength + AUDIO_GAIN.glowStrength * g - m.glowStrength) * k;
    m.rimStrength += (base.rimStrength + AUDIO_GAIN.rimStrength * g - m.rimStrength) * k;
    m.brightness += (base.brightness + AUDIO_GAIN.brightness * g - m.brightness) * k;

    // Colour theme: the phase runs faster the louder the voice, so speech visibly cycles through
    // the palette while a quiet orb barely moves it.
    this.huePhase += dt * (0.12 + 1.4 * m.audioIntensity);
    const hueTarget = HUE_BASE[state] + (speaking ? HUE_SWING * Math.sin(this.huePhase) : 0.25 * Math.sin(this.huePhase * 0.5));
    m.hue += (hueTarget - m.hue) * ease(dt, 0.35);

    this.previous = state;
    return m;
  }
}
